import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { atom, useAtomValue } from 'jotai';
import { shallow } from 'zustand/shallow';
import {
	Design,
	DesignSide,
	ProductPrintType,
	useZakekeCollages,
	useZakekeEngraving,
	useZakekeHelpers,
	useZakekeImageGalleriesAvailable,
	useZakekeMasks,
	useZakekePrice,
	useZakekeSettings,
	useZakekeShapes,
	useZakekeShapesAndMasks,
} from '@zakeke/zakeke-customizer-react';

import {
	BackgroundRule,
	CustomizerElement,
	isImageElement,
	isShapeElement,
	isTextArtElement,
	isTextElement,
} from '../components/interfaces';
import { checkConstraints } from './utils';
import { checkTextStrokeRestrictions } from '../shared/helpers.text';
import { itemsSelectorFamily, useAppStore, useGetItems } from './store';
import { createWithEqualityFn, UseBoundStoreWithEqualityFn } from 'zustand/traditional';
import type { StoreApi } from 'zustand/vanilla';
import usePrintMethods from '../hooks/usePrintMethods';
import usePhotoEditorTool from '../hooks/usePhotoEditorTool';
import { useBackgroundEditingTools } from '../hooks/useBackgroundEditingTools';
import useDebouncedEffect from '../components/app/bulk/hooks/useDebouncedEffect';
import { selectAtom } from 'jotai/utils';

/**
 * Get the list of supported file formats to upload
 * @param restrictions A design, product print type or design side (all extends the common upload restrictions)
 * @returns a list of supported file formats
 */
const getUploadFileFormats = (restrictions: ProductPrintType | Design | DesignSide | null) => {
	if (!restrictions) return [];
	const uploadFileFormats: string[] = [];

	if (restrictions.isPngUploadEnabled) {
		uploadFileFormats.push('.png');
		uploadFileFormats.push('.heic');
		uploadFileFormats.push('.webp');
	}

	if (restrictions.isJpgUploadEnabled) {
		uploadFileFormats.push('.jpg');
		uploadFileFormats.push('.jpeg');
	}

	if (restrictions.isSvgUploadEnabled) uploadFileFormats.push('.svg');

	if (restrictions.isAiUploadEnabled) uploadFileFormats.push('.ai');

	if (restrictions.isPdfUploadEnabled) uploadFileFormats.push('.pdf');

	if (restrictions.isEpsUploadEnabled) uploadFileFormats.push('.eps');

	return uploadFileFormats;
};

const allowedFileType = {
	1: ['.jpeg', '.jpg', '.png'],
	2: ['.jpeg', '.jpg', '.png', '.svg'],
};

type AllowedFileTypeForBackground = keyof typeof allowedFileType;

const filterUploadForBackground = (backgroundRules: BackgroundRule | undefined, allowedUploadFile: string[]) => {
	if (backgroundRules && backgroundRules?.imageTypeID) {
		const allowedBackground = allowedFileType[backgroundRules.imageTypeID as AllowedFileTypeForBackground];
		return allowedBackground.filter((value) => allowedUploadFile.includes(value));
	} else {
		return [''];
	}
};

export const areItemsPrintableAtom = atom((get) =>
	get(itemsSelectorFamily).some((x) => x.isFromTemplate !== true || x.constraints?.isPrintable !== false)
);

const currentNumberOfImagesForSideAtom = atom((get) => {
	return get(itemsSelectorFamily).filter(isImageElement).reduce((pre, item) => {
		const sideId = item.sideId;
		pre.set(sideId, (pre.get(sideId) || 0) + 1);
		return pre;
	}, new Map<number, number>());
});

const currentNumberOfTextsForSideAtom = atom((get) => {
	return get(itemsSelectorFamily).filter((x) => isTextElement(x) || isTextArtElement(x)).reduce((pre, item) => {
		const sideId = item.sideId;
		pre.set(sideId, (pre.get(sideId) || 0) + 1);
		return pre;
	}, new Map<number, number>());
});

const _isItemStatic = (
	item: CustomizerElement,
	availableTools: {
		canChangeFontColor: boolean;
		canChangeTextStrokeColor?: boolean;
		canChangeTextStrokeWidth?: boolean;
	}
): boolean => {
	if (!item.constraints) return false;

	let isStatic = !(
		(item.constraints.canMove ?? true) ||
		(item.constraints.canRotate ?? true) ||
		(item.constraints.canResize ?? true) ||
		(item.constraints.canDelete ?? true) ||
		(item.constraints.canTransform ?? true)
	);

	if (isImageElement(item)) {
		isStatic = isStatic && !(item.constraints.canEdit ?? true); // can Edit on image for repleace
	}

	if (isImageElement(item) && item.isVector) {
		isStatic = isStatic && !(item.constraints.canChangeSvgColors ?? true);
	}

	if (isShapeElement(item) && item?.isFillable === true) {
		isStatic = isStatic && true;
	}

	if (
		isShapeElement(item) &&
		item?.isFillable !== true &&
		item?.constraints?.canChangeShapeFillColor === false &&
		item?.constraints?.canChangeShapeStrokeColor === false
	) {
		isStatic = isStatic && true;
	}

	if (isTextElement(item)) {
		isStatic =
			isStatic &&
			!(
				(item.constraints.canEdit ?? true) ||
				((item.constraints.canChangeTextStrokeColor ?? true) && availableTools.canChangeTextStrokeColor) ||
				((item.constraints.canChangeTextStrokeWidth ?? true) && availableTools.canChangeTextStrokeWidth) ||
				(item.constraints.canChangeFontFamily ?? true) ||
				((item.constraints.canChangeFontColor ?? true) && availableTools.canChangeFontColor) ||
				(item.constraints.canChangeFontWeight ?? true) ||
				(item.constraints.canChangeTextPathMode ?? true)
			);
	}

	return isStatic;
};

/**
 * check if item is immutable, not sortable, not deletable and not replaceable.
 * check if item is a fixed element setted from seller (like bleeding edges or legal info)
 */
const _getIsFixedLayoutItem = (
	item: CustomizerElement,
	availableTools: {
		isReplaceEnabled: boolean;
		isDuplicateEnabled: boolean;
		isMoveFowardBackwardEnabled: boolean;
		isDeleteEnabled: boolean;
		canChangeFontColor: boolean;
		canChangeTextStrokeColor?: boolean;
		canChangeTextStrokeWidth?: boolean;
	}
) => {
	//const isItemBackground = isImageElement(item) && item.isBackgroundForArea && item.isBackgroundForArea > 0;
	const isItemReplaceableImage = isImageElement(item) && availableTools.isReplaceEnabled === true;
	if (isItemReplaceableImage) return false;

	return (
		_isItemStatic(item, availableTools) &&
		(item.constraints?.isAlwaysOnBottom ||
			item.constraints?.isAlwaysOnTop ||
			(isImageElement(item) && item.isBackgroundForArea && item.isBackgroundForArea > 0)) &&
		!availableTools.isDuplicateEnabled &&
		!availableTools.isMoveFowardBackwardEnabled &&
		!availableTools.isDeleteEnabled
	);
};

export type AvailableTools = ReturnType<ReturnType<typeof useGetAvailableToolCallback>>;

const useGetAvailableToolCallback = () => {
	const getItems = useGetItems();
	const { getPrintMethodForSide } = usePrintMethods();
	const { getSelectableColorsForText } = useZakekeHelpers();
	const [price] = useZakekePrice();
	const shapesAndMasks = useZakekeShapesAndMasks();
	const masks = useZakekeMasks();
	const shapes = useZakekeShapes();
	const imageGalleriesAvailable = useZakekeImageGalleriesAvailable();
	const { photoEditorLabelsFromBackend, photoEditorToolsList } = usePhotoEditorTool();
	const { isOrderDesignEditor, isInTestMode, isShopifyDevelopmentStore, isImageScalingEnabled } = useZakekeSettings();

	const collages = useZakekeCollages();
	const [selectedSideId, currentTemplate, sidesCollages] = useAppStore((x) => [
		x.selectedSideId,
		x.currentTemplate,
		x.sidesCollages,
	]);
	const backgroundRules = useBackgroundEditingTools();
	const { printTypesWithEngraving } = useZakekeEngraving();
	// TODO: Optimize dependency from 'items'
	const areItemsPrintable = useAtomValue(areItemsPrintableAtom);
	const currentNumberOfImagesForSide = useAtomValue(currentNumberOfImagesForSideAtom);
	const currentNumberOfTextsForSide = useAtomValue(currentNumberOfTextsForSideAtom);

	return useCallback(
		(item?: CustomizerElement | null) => {
			const currentPrintType = getPrintMethodForSide(selectedSideId);

			const areSapesAndMaskForPrintTypeEnabled =
				shapesAndMasks?.printTypesIDs.some((el: Number) => el === currentPrintType?.id) ?? false;
			const areShapesEnabled =
				(shapesAndMasks?.forProduct || areSapesAndMaskForPrintTypeEnabled) && shapes.length > 0;
			const areMasksEnabled =
				(shapesAndMasks?.forProduct || areSapesAndMaskForPrintTypeEnabled) && masks.length > 0;

			const collage = sidesCollages.get(selectedSideId)
				? collages.find((x) => x.id === sidesCollages.get(selectedSideId))
				: undefined;
			if (!currentPrintType)
				throw new Error(
					'Ensure a print type is selected for the side ' +
						selectedSideId +
						' before using useAvailableTools.'
				);

			const currentTemplateSide = currentTemplate?.sides.find((x) => x.sideId === selectedSideId);

			const restrictions =
				!currentTemplateSide && !currentTemplate
					? currentPrintType
					: currentTemplateSide ?? currentTemplate ?? currentPrintType;

			let uploadFileFormats: string[] = getUploadFileFormats(restrictions) ?? [];
			const items = getItems();
			// TODO: Optimize dependency from 'items'
			const filledCollageBoxes = items
				.filter((x) => x.sideId === selectedSideId)
				.filter((x) => x.collageBoxId !== null);
			const isCollageFilled = collage ? filledCollageBoxes.length >= collage.boxesCount : false;
			const hasCollage = !!collage;

			const maxNumberOfImages =
				currentTemplateSide?.maxNumberOfImages ??
				currentTemplate?.maxNumberOfImages ??
				currentPrintType.maxNumberOfImages;
			const isSideMaxNumberOfImages = !!currentTemplateSide?.maxNumberOfImages; 
			// se abbiamo currentTemplateSide allora prendiamo in considerazione solo il singolo side, sennò l'intero prodotto
			const currentNumberOfImages = isSideMaxNumberOfImages ? (currentNumberOfImagesForSide.get(selectedSideId) ?? 0) : Array.from(currentNumberOfImagesForSide.values()).reduce((a, b) => a + b, 0);

			const maxNumberOfTexts =
				currentTemplateSide?.maxNumberOfTexts ??
				currentTemplate?.maxNumberOfTexts ??
				currentPrintType.maxNumberOfTexts;
			const isSideMaxNumberOfTexts = !!currentTemplateSide?.maxNumberOfTexts;
			// se abbiamo currentTemplateSide allora prendiamo in considerazione solo il singolo side, sennò l'intero prodotto
			const currentNumberOfTexts = isSideMaxNumberOfTexts ? (currentNumberOfTextsForSide.get(selectedSideId) ?? 0) : Array.from(currentNumberOfTextsForSide.values()).reduce((a, b) => a + b, 0);

			const sellerImageEnabled =
				currentPrintType.areClipartsImagesEnabled &&
				(currentTemplateSide
					? currentTemplateSide.areClipartsImagesEnabled
					: currentTemplate
					? currentTemplate?.areClipartsImagesEnabled
					: true);

			const areCustomersImagesEnabled = currentPrintType.areCustomersImagesEnabled; // GUIDO - DA RPINT METHOD

			const isMaxNumberImagesReached =
				(maxNumberOfImages && currentNumberOfImages >= maxNumberOfImages) || isCollageFilled;
			const isMaxNumberTextsReached = maxNumberOfTexts && currentNumberOfTexts >= maxNumberOfTexts;

			const [printTypeEffect] = printTypesWithEngraving.filter((x) => x.id === currentPrintType.id);
			const hasPrintTypePreviewEffect = !!printTypeEffect;
			const canEngravingEffectChangeTextColor =
				hasPrintTypePreviewEffect && printTypeEffect.previewDesignEffect.hasTextOverlay ? false : true;
			const canEngravingEffectChangeImageColor =
				hasPrintTypePreviewEffect &&
				printTypeEffect.previewDesignEffect.imageEffectTypeName.toLowerCase() === 'none'
					? false
					: true;

			const isLargeFileEnabled = currentPrintType.isLargeFileEnabled;

			// const isUploadAllFileEnabledCurrentTemplate =
			// 	currentTemplate?.isJpgUploadEnabled &&
			// 	currentTemplate?.isPngUploadEnabled &&
			// 	currentTemplate?.isSvgUploadEnabled &&
			// 	currentTemplate?.isEpsUploadEnabled &&
			// 	currentTemplate?.isAiUploadEnabled &&
			// 	currentTemplate?.isAiUploadEnabled &&
			// 	currentTemplate?.isFacebookEnabled &&
			// 	currentTemplate?.isPdfWithRasterEnabled;

			const isUploadAnyFileEnabledCurrentTemplateSide =
				(currentTemplateSide?.isJpgUploadEnabled ||
					currentTemplateSide?.isPngUploadEnabled ||
					currentTemplateSide?.isSvgUploadEnabled ||
					currentTemplateSide?.isEpsUploadEnabled ||
					currentTemplateSide?.isAiUploadEnabled ||
					currentTemplateSide?.isAiUploadEnabled ||
					currentTemplateSide?.isFacebookEnabled ||
					currentTemplateSide?.isPdfWithRasterEnabled);

			const isUploadAnyFileEnabledCurrentTemplate =
				(currentTemplate?.isJpgUploadEnabled ||
					currentTemplate?.isPngUploadEnabled ||
					currentTemplate?.isSvgUploadEnabled ||
					currentTemplate?.isEpsUploadEnabled ||
					currentTemplate?.isAiUploadEnabled ||
					currentTemplate?.isAiUploadEnabled ||
					currentTemplate?.isFacebookEnabled ||
					currentTemplate?.isPdfWithRasterEnabled);

			const isUploadAnyFileEnabledCurrentPrintType =
				currentPrintType.areCustomersImagesEnabled &&
				(currentPrintType?.isJpgUploadEnabled ||
					currentPrintType?.isPngUploadEnabled ||
					currentPrintType?.isSvgUploadEnabled ||
					currentPrintType?.isEpsUploadEnabled ||
					currentPrintType?.isAiUploadEnabled ||
					currentPrintType?.isFacebookEnabled ||
					currentPrintType?.isPremiumPhotosEnabled);

			const isAnyUploadEnabled =
				isUploadAnyFileEnabledCurrentTemplateSide ??
				isUploadAnyFileEnabledCurrentTemplate ??
				isUploadAnyFileEnabledCurrentPrintType;

			const isPremiumPhotosEnabled =
				!isOrderDesignEditor &&
				!isInTestMode &&
				!isShopifyDevelopmentStore &&
				price > 0 &&
				currentPrintType.isPremiumPhotosEnabled &&
				(currentPrintType.isPngUploadEnabled || currentPrintType.isJpgUploadEnabled);

			const templateImageEnabled =
				!currentTemplate || currentTemplateSide?.isImageEnabled || currentTemplate?.isImageEnabled;

			const templateFileUploadEnabled =
				!currentTemplate || isUploadAnyFileEnabledCurrentTemplateSide || isUploadAnyFileEnabledCurrentTemplate;

			let areCustomersUploadImagesEnabled =
				areCustomersImagesEnabled &&
				((currentPrintType?.isJpgUploadEnabled && currentPrintType.isImageEnabled && templateImageEnabled) ||
					(templateFileUploadEnabled && templateImageEnabled && currentPrintType.isImageEnabled));

			const isClipartsImagesButtonEnabled =
				(!currentTemplate ||
					(currentTemplateSide?.isImageEnabled ?? currentTemplate?.isImageEnabled ?? true)) &&
				currentPrintType.isImageEnabled &&
				imageGalleriesAvailable &&
				sellerImageEnabled;

			const areClipartsImagesEnabled = imageGalleriesAvailable && sellerImageEnabled;

			/* Text */

			const isTextEnabled =
				currentPrintType.isTextEnabled &&
				(!currentTemplate || (currentTemplateSide?.isTextEnabled ?? currentTemplate?.isTextEnabled));

			const isTextArtEnabled = isTextEnabled && currentPrintType.isTextArtEnabled;

			// since editing image uploads it, we need to check if upload is enabled before enabling image filters
			const isImageFiltersAndEditingEnabled =
				areCustomersUploadImagesEnabled && currentPrintType.isImageFiltersAndEditingEnabled;

			const canPrintTypeChangeFontColor = canEngravingEffectChangeTextColor && true;
			const canPrintTypeChangeFontFamily = true;
			const canPrintTypeChangeFontSize =
				currentPrintType.isTextFontSizeEnabled &&
				((currentPrintType.minTextFontSize === null && currentPrintType.maxTextFontSize === null) ||
					currentPrintType.minTextFontSize !== currentPrintType.maxTextFontSize);
			const canPrintTypeChangeLineSpacing =
				currentPrintType.isTextLineSpacingEnabled &&
				((currentPrintType.minTextLineSpacing === null && currentPrintType.maxTextLineSpacing === null) ||
					currentPrintType.minTextLineSpacing !== currentPrintType.maxTextLineSpacing);
			const canPrintTypeChangeLetterSpacing =
				currentPrintType.isTextLetterSpacingEnabled &&
				((currentPrintType.minTextLetterSpacing === null && currentPrintType.maxTextLetterSpacing === null) ||
					currentPrintType.minTextLetterSpacing !== currentPrintType.maxTextLetterSpacing);
			const canPrintTypeChangeAlignmnet =
				currentPrintType.isTextLeftAlignmentEnabled ||
				currentPrintType.isTextRightAlignmentEnabled ||
				currentPrintType.isTextCenterAlignmentEnabled ||
				currentPrintType.isTextJustifyAlignmentEnabled ||
				currentPrintType.isTextTopVerticalAlignmentEnabled ||
				currentPrintType.isTextMiddleVerticalAlignmentEnabled ||
				currentPrintType.isTextBottomVerticalAlignmentEnabled;

			const canPrintTypeChangeVerticalAlignment =
				currentPrintType.isTextTopVerticalAlignmentEnabled ||
				currentPrintType.isTextMiddleVerticalAlignmentEnabled ||
				currentPrintType.isTextBottomVerticalAlignmentEnabled;

			const canPrintTypeChangeShadowAngle =
				(currentPrintType.minShadowAngle === null && currentPrintType.maxShadowAngle == null) ||
				currentPrintType.minShadowAngle !== currentPrintType.maxShadowAngle;
			const canPrintTypeChangeShadowBlur =
				(currentPrintType.minShadowBlur === null && currentPrintType.maxShadowBlur == null) ||
				currentPrintType.minShadowBlur !== currentPrintType.maxShadowBlur;
			const canPrintTypeChangeShadowDistance =
				(currentPrintType.minShadowDistance === null && currentPrintType.maxShadowDistance == null) ||
				currentPrintType.minShadowDistance !== currentPrintType.maxShadowDistance;
			const canPrintTypeChangeShadowColor =
				currentPrintType.shadowColors == null ||
				currentPrintType.shadowColors.length === 0 ||
				currentPrintType.shadowColors.length > 1;

			const isTextUppercase =
				item?.constraints?.toUppercase ??
				(!!item && currentPrintType.isTextUppercase && !item?.isFromTemplate) ??
				false;

			/* Background editing tool */
			const isBackgroundEditingEnabled = backgroundRules ? true : false;

			/* TextArt */
			// enable text art color, stroke color, stroke width
			const canChangeTextArtColor = canEngravingEffectChangeTextColor;

			/* Final checks (print method + item constraint) */
			const canChangeFontColor =
				canEngravingEffectChangeTextColor &&
				(item?.constraints?.canChangeFontColor ?? canPrintTypeChangeFontColor);
			const canChangeFontFamily = item?.constraints?.canChangeFontFamily ?? canPrintTypeChangeFontFamily;
			const canChangeFontSize =
				item && item.constraints
					? (item?.constraints?.canResize ?? true) && (item?.constraints?.canChangeFontSize ?? true)
					: canPrintTypeChangeFontSize;
			const canChangeFontWeight = item?.constraints?.canChangeFontWeight ?? currentPrintType.isTextBoldEnabled; //in tpl item could check "Font weight/italic", that only sets canChangeFontWeight
			const canChangeFontItalic = item?.constraints?.canChangeFontWeight ?? currentPrintType.isTextItalicEnabled; //in tpl item could check "Font weight/italic", that only sets canChangeFontWeight
			const canChangeLineSpacing = item?.constraints?.canChangeLineSpacing ?? canPrintTypeChangeLineSpacing;
			const canChangeLetterSpacing = item?.constraints?.canChangeLetterSpacing ?? canPrintTypeChangeLetterSpacing;
			const canChangeAlignmentOptions = item?.constraints?.canChangeJustification ?? canPrintTypeChangeAlignmnet;
			const canChangeAlignmentLeft =
				item?.constraints?.canChangeJustification ?? currentPrintType.isTextLeftAlignmentEnabled;
			const canChangeAlignmentCenter =
				item?.constraints?.canChangeJustification ?? currentPrintType.isTextCenterAlignmentEnabled;
			const canChangeAlignmentRight =
				item?.constraints?.canChangeJustification ?? currentPrintType.isTextRightAlignmentEnabled;
			const canChangeAlignmentJustify =
				item?.constraints?.canChangeJustification ?? currentPrintType.isTextJustifyAlignmentEnabled;
			const canChangeAlignment =
				canChangeAlignmentOptions &&
				[
					canChangeAlignmentLeft,
					canChangeAlignmentCenter,
					canChangeAlignmentRight,
					canChangeAlignmentJustify,
				].filter(Boolean).length >= 2;
			const canChangeVerticalAlignmentOptions =
				item?.isTextBox &&
				(item?.constraints?.canChangeVerticalAlignment ?? canPrintTypeChangeVerticalAlignment ?? true);
			const canChangeVerticalAlignmentTop =
				item?.constraints?.canChangeVerticalAlignment ?? currentPrintType.isTextTopVerticalAlignmentEnabled;
			const canChangeVerticalAlignmentMiddle =
				item?.constraints?.canChangeVerticalAlignment ?? currentPrintType.isTextMiddleVerticalAlignmentEnabled;
			const canChangeVerticalAlignmentBottom =
				item?.constraints?.canChangeVerticalAlignment ?? currentPrintType.isTextBottomVerticalAlignmentEnabled;
			const canChangeVerticalAlignment =
				canChangeVerticalAlignmentOptions &&
				[
					canChangeVerticalAlignmentTop,
					canChangeVerticalAlignmentMiddle,
					canChangeVerticalAlignmentBottom,
				].filter(Boolean).length >= 2;
			const canChangeTextBoxMode =
				item?.constraints?.canChangeTextBoxMode ?? currentPrintType.isTextBoxEnabled ?? true;
			const isMaskEnabled = areMasksEnabled && (item?.constraints?.canAddMask ?? true);

			const isUploadEnabled = currentPrintType.areCustomersImagesEnabled; // DA PRINT METHODS
			const isReplaceEnabled =
				!!item &&
				isImageElement(item) &&
				(item?.constraints?.canEdit ?? true) &&
				(sellerImageEnabled || isUploadEnabled);
			const isMaskReplaceable = item?.constraints?.canReplaceMask ?? true;
			const isMaskDeletable = item?.constraints?.canDeleteMask ?? true;
			const canImageFiltersAndEditing =
				isImageFiltersAndEditingEnabled &&
				item &&
				isImageElement(item) &&
				!item.isVector &&
				!item.isBackgroundForArea;

			const canItemChangeTextPathMode = item?.constraints?.canChangeTextPathMode ?? true;
			const canPrintTypeChangeCurvedText = currentPrintType.isTextCurved;
			const canChangeTextPathMode = canItemChangeTextPathMode && canPrintTypeChangeCurvedText;

			const isDeletable = item?.constraints?.canDelete ?? true;

			let isDuplicateEnabled =
				!(item && isImageElement(item) && item?.isBackgroundForArea && item?.isBackgroundForArea > 0) &&
				(item?.constraints?.canTransform ?? true) &&
				currentPrintType.isDuplicateEnabled;

			let isMoveable = item?.constraints?.canMove ?? true;

			let isMoveFowardBackwardEnabled =
				item?.constraints?.isAlwaysOnTop || item?.constraints?.isAlwaysOnBottom
					? false
					: !(item && isImageElement(item) && item?.isBackgroundForArea && item?.isBackgroundForArea > 0) &&
					  isMoveable &&
					  (item?.constraints?.canTransform ?? true) &&
					  currentPrintType.isMoveFowardBackwardEnabled;

			let isFlipEnabled =
				!(item && isImageElement(item) && item?.isBackgroundForArea && item?.isBackgroundForArea > 0) &&
				(item?.constraints?.canTransform ?? true) &&
				currentPrintType.isFlipEnabled;

			if (item && isTextElement(item)) isFlipEnabled = false; // Flip don't work on texts

			if (item && isTextElement(item) && (!isTextEnabled || isMaxNumberTextsReached)) isDuplicateEnabled = false;

			if (item && isTextArtElement(item) && (!isTextEnabled || !isTextArtEnabled || isMaxNumberTextsReached))
				isDuplicateEnabled = false;

			if (item && isImageElement(item) && (!areCustomersUploadImagesEnabled || isMaxNumberImagesReached))
				isDuplicateEnabled = false;

			let maxListFontSize;
			if (currentPrintType.fontSizeList && currentPrintType.fontSizeList.length > 0) {
				maxListFontSize = currentPrintType.fontSizeList[currentPrintType.fontSizeList.length - 1];
			}

			/* questo valore è dato in punti tipografici, ma noi passiamo alla libreria un valore in px */
			const minFontSize = item?.constraints?.minFontSize ?? currentPrintType.minTextFontSize ?? 1;
			const maxFontSize =
				item?.constraints?.maxFontSize ?? currentPrintType.maxTextFontSize ?? maxListFontSize ?? undefined;
			const fontSizeList = currentPrintType.fontSizeList;
			const minLineSpacing = item?.constraints?.minLineSpacing ?? currentPrintType.minTextLineSpacing ?? -100;
			const maxLineSpacing = item?.constraints?.maxLineSpacing ?? currentPrintType.maxTextLineSpacing ?? 100;
			const minLetterSpacing =
				item?.constraints?.minLetterSpacing ?? currentPrintType.minTextLetterSpacing ?? -100;
			const maxLetterSpacing =
				item?.constraints?.maxLetterSpacing ?? currentPrintType.maxTextLetterSpacing ?? 100;

			const canPrintTypeRecolorImage = canEngravingEffectChangeImageColor;

			const canItemChangeShadow =
				item &&
				(!item.constraints ||
					item.constraints.canChangeShadowAngle ||
					item.constraints.canChangeShadowBlur ||
					item.constraints.canChangeShadowColor ||
					item.constraints.canChangeShadowDistance);

			const canChangeShadow = currentPrintType.isTextShadowsEnabled && canItemChangeShadow;
			const canChangeShadowAngle =
				canChangeShadow && checkConstraints(item, 'canChangeShadowAngle') && canPrintTypeChangeShadowAngle;
			const canChangeShadowBlur =
				canChangeShadow && checkConstraints(item, 'canChangeShadowBlur') && canPrintTypeChangeShadowBlur;
			const canChangeShadowDistance =
				canChangeShadow &&
				checkConstraints(item, 'canChangeShadowDistance') &&
				canPrintTypeChangeShadowDistance;
			const canChangeShadowColor =
				canChangeShadow && checkConstraints(item, 'canChangeShadowColor') && canPrintTypeChangeShadowColor;

			// performance consuming calculation
			const getDefaultShadowColor = () => {
				let defaultShadowColor = currentPrintType.defaultShadowColor;
				const shadowSelectableColors =
					item && isTextElement(item)
						? getSelectableColorsForText(item.sideId, item.shadowColor ?? null)
						: null;
				if (
					shadowSelectableColors &&
					shadowSelectableColors.length > 0 &&
					!shadowSelectableColors.find((x) => x.hex === defaultShadowColor)
				)
					defaultShadowColor = shadowSelectableColors[0].hex;
				return defaultShadowColor;
			};

			const areSellerImagesGalleryEnabled = backgroundRules?.enableImageSellerGallery ? true : false;
			const areBackgroundCustomersImagesEnabled = backgroundRules?.enableImageCustomerGallery ? true : false;

			const uploadFileFormatsBackground = () => {
				return filterUploadForBackground(backgroundRules, uploadFileFormats);
			};

			const isUploadFromYourComputerVisible = uploadFileFormats.length > 0;

			const isImagesScalingEnabled = currentPrintType.customerImagesScalingType === 1;
			const isClipartScalingEnabled = currentPrintType.clipartImagesScalingType === 1;
			const isClipartsResizeEnabled = currentPrintType.isClipartsImagesResizeEnabled;

			const canImageChangeColors =
				item?.constraints?.canChangeSvgColors ?? currentPrintType.isSvgRecolorEnabled ?? true;

			// ok ma il vecchio puntava solo sui files SVG !
			const canSVGImageChangeColors =
				canImageChangeColors &&
				item &&
				isImageElement(item) &&
				(item.isClipart || item.isVector) &&
				!!item.colors.length;

			const canShapeCangeFillColor = item?.constraints?.canChangeShapeFillColor ?? true;
			const canShapeChangeStrokeColor = item?.constraints?.canChangeShapeStrokeColor ?? true;
			const canShapeChangeStrokeWidth = item?.constraints?.canChangeShapeStrokeWidth ?? true;

			const canShapeChangeColors =
				item && isShapeElement(item) && canShapeCangeFillColor && item?.isFillable === false;

			const textStrokeRestrictions = checkTextStrokeRestrictions(item, currentPrintType, {
				canEngravingEffectChangeTextColor,
			});

			const isItemStatic =
				!!item &&
				_isItemStatic(item, {
					canChangeFontColor,
					canChangeTextStrokeColor: textStrokeRestrictions.canChangeTextStrokeColor,
					canChangeTextStrokeWidth: textStrokeRestrictions.canChangeTextStrokeWidth,
				});
			const isFixedLayoutItem =
				!!item &&
				_getIsFixedLayoutItem(item, {
					isReplaceEnabled: !!isReplaceEnabled,
					isDuplicateEnabled,
					isMoveFowardBackwardEnabled,
					isDeleteEnabled: isDeletable,
					canChangeFontColor,
					canChangeTextStrokeWidth: textStrokeRestrictions.canChangeTextStrokeWidth,
					canChangeTextStrokeColor: textStrokeRestrictions.canChangeTextStrokeColor,
				});
			const filteredPhotoEditorLabels = photoEditorLabelsFromBackend.filter((label) =>
				currentPrintType.imageEditingToolSettingsToExclude?.every((labelToExclude) => label !== labelToExclude)
			);

			const availablePhotoEditorTools = filteredPhotoEditorLabels.map((label) => photoEditorToolsList[label]);

			const canResizeConstraint = item?.constraints?.canResize !== false;

			const blockedTemplateBackground = items.some((x) => {
				//we check only canDelete, since canEdit - replace - should also be managed from background edit tool
				return (
					x.sideId === selectedSideId &&
					isImageElement(x) &&
					x.isBackgroundForArea &&
					x.isBackgroundForArea > 0 &&
					x.constraints?.canEdit === false &&
					(sellerImageEnabled || isUploadEnabled)
				);
			});

			return {
				/* Tools */
				areShapesEnabled,
				areMasksEnabled,
				areCustomersUploadImagesEnabled,
				areClipartsImagesEnabled,
				isClipartsImagesButtonEnabled,
				areSellerImagesGalleryEnabled,
				areBackgroundCustomersImagesEnabled,
				isTextEnabled,
				isTextArtEnabled,
				isBackgroundEditingEnabled,
				maxNumberOfImages,
				currentNumberOfImages,
				isMaxNumberImagesReached,
				maxNumberOfTexts,
				currentNumberOfTexts,
				isMaxNumberTextsReached,
				isCollageFilled,
				hasCollage,
				canResizeConstraint,
				blockedTemplateBackground,

				/* templates */
				currentTemplate,
				currentTemplateSide,

				/* Accessories */
				isPdfPreviewEnabled: currentPrintType.isPdfPreviewEnabled,

				/* Element actions  */
				isDeletable,
				isDuplicateEnabled,
				isFlipEnabled,
				isMoveable,
				isMoveFowardBackwardEnabled,
				isReplaceEnabled,
				isMaskEnabled,
				isMaskReplaceable,
				isMaskDeletable,
				canImageFiltersAndEditing,
				areItemsPrintable,

				/* Images upload */
				isAnyUploadEnabled,
				uploadFileFormats,
				uploadFileFormatsBackground,
				isUploadFromYourComputerVisible,
				isLargeFileEnabled,
				isPremiumPhotosEnabled,
				isFacebookEnabled:
					currentTemplateSide?.isFacebookEnabled ??
					currentTemplate?.isFacebookEnabled ??
					currentPrintType.isFacebookEnabled,
				isImagesScalingEnabled,
				isClipartScalingEnabled,
				isClipartsResizeEnabled,
				templateImageEnabled,

				/* PDF, EPS, AI */
				isPdfWithRasterEnabled:
					currentTemplateSide?.isPdfWithRasterEnabled ??
					currentTemplate?.isPdfWithRasterEnabled ??
					currentPrintType.isPdfWithRasterEnabled,
				isLargePdfResizeEnabled:
					currentTemplateSide?.isLargePdfResizeEnabled ??
					currentTemplate?.isLargePdfResizeEnabled ??
					currentPrintType.isLargePdfResizeEnabled,
				isVectorPdfToRasterConversionEnabled: currentPrintType.isVectorPdfToRasterConversionEnabled,
				isVectorEpsToRasterConversionEnabled: currentPrintType.isVectorEpsToRasterConversionEnabled,
				isVectorAiToRasterConversionEnabled: currentPrintType.isVectorAiToRasterConversionEnabled,
				isSvgRecolorEnabled: currentPrintType.isSvgRecolorEnabled,
				// canImageChangeColors,
				canSVGImageChangeColors,

				// SVG recolor
				canPrintTypeRecolorImage,

				hasPrintTypePreviewEffect,

				// Shapes
				canShapeCangeFillColor,
				canShapeChangeStrokeColor,
				canShapeChangeStrokeWidth,
				canShapeChangeColors,

				// TextArt
				canChangeTextArtColor,

				// Text
				canChangeFontColor,
				canChangeFontFamily,
				canChangeFontSize,
				canChangeFontWeight,
				canChangeFontItalic,
				canChangeLineSpacing,
				canChangeLetterSpacing,
				canChangeAlignment,
				canChangeAlignmentLeft,
				canChangeAlignmentCenter,
				canChangeAlignmentRight,
				canChangeAlignmentJustify,
				canChangeVerticalAlignment,
				canChangeVerticalAlignmentTop,
				canChangeVerticalAlignmentMiddle,
				canChangeVerticalAlignmentBottom,
				canChangeTextBoxMode,
				canChangeTextPathMode,

				isTextUppercase,

				minFontSize,
				maxFontSize,
				fontSizeList,
				minLineSpacing,
				maxLineSpacing,
				minLetterSpacing,
				maxLetterSpacing,
				canChangeShadow,
				canChangeShadowAngle,
				canChangeShadowBlur,
				canChangeShadowDistance,
				canChangeShadowColor,
				getDefaultShadowColor,

				// printType restrictions
				imageEditingToolSettingsToExclude: currentPrintType.imageEditingToolSettingsToExclude,
				isFixedLayoutItem,
				isItemStatic,
				filteredPhotoEditorLabels,
				availablePhotoEditorTools,
				areCustomersImagesEnabled,
				isImageScalingEnabled,
				...textStrokeRestrictions,
			};
		},
		[
			areItemsPrintable,
			backgroundRules,
			collages,
			currentTemplate,
			getItems,
			getPrintMethodForSide,
			getSelectableColorsForText,
			imageGalleriesAvailable,
			masks.length,
			photoEditorToolsList,
			photoEditorLabelsFromBackend,
			price,
			printTypesWithEngraving,
			selectedSideId,
			shapes.length,
			shapesAndMasks?.forProduct,
			shapesAndMasks?.printTypesIDs,
			sidesCollages,
			isOrderDesignEditor,
			isInTestMode,
			isShopifyDevelopmentStore,
			isImageScalingEnabled,
			currentNumberOfImagesForSide,
			currentNumberOfTextsForSide,
		]
	);
};

type AvailableToolsStore = {
	global: AvailableTools;
	byGuids: Map<string, AvailableTools>;
	getByGuids: (guid: string) => AvailableTools;
	activeListenersCount: Map<string, number>;
};
export const AvailableToolsContext = React.createContext<UseBoundStoreWithEqualityFn<
	StoreApi<AvailableToolsStore>
> | null>(null);

// atoms with selector to allow update only when props of item used to compute available tools changes
// if useGetAvailableToolCallback is updated probably this should be updated too
const availableToolsItemsAtom = selectAtom(
	itemsSelectorFamily,
	(items) => items,
	(prev, curr) => {
		if (prev === curr) {
			return true;
		}
		if (prev.length !== curr.length) {
			return false;
		}
		const allGuids = new Set([...prev.map((x) => x.guid), ...curr.map((x) => x.guid)]);
		for (let guid of allGuids) {
			const prevItem = prev.find((x) => x.guid === guid);
			const currItem = curr.find((x) => x.guid === guid);
			if (!prevItem || !currItem) {
				return false;
			}
			if (prevItem === currItem) {
				return true;
			}
			if (prevItem.constraints !== currItem.constraints) {
				return false;
			}
			if (prevItem.sideId !== currItem.sideId) {
				return false;
			}
			if (isImageElement(prevItem) && isImageElement(currItem)) {
				if (prevItem.isVector !== currItem.isVector) {
					return false;
				}
				if (prevItem.isClipart !== currItem.isClipart) {
					return false;
				}
				if (prevItem.isBackgroundForArea !== currItem.isBackgroundForArea) {
					return false;
				}
			}
			if (isTextElement(prevItem) && isTextElement(currItem)) {
				if (prevItem.shadowColor !== currItem.shadowColor) {
					return false;
				}
				if (prevItem.isTextBox !== currItem.isTextBox) {
					return false;
				}
			}
		}
		return true;
	}
);
type AvailableToolsProviderProps = {
	children: React.ReactNode;
};
export const AvailableToolsProvider: React.FC<AvailableToolsProviderProps> = ({ children }) => {
	const getAvailableToolsForItem = useGetAvailableToolCallback();
	const getAvailableToolsForItemRef = useRef(getAvailableToolsForItem);
	useEffect(() => {
		getAvailableToolsForItemRef.current = getAvailableToolsForItem;
	}, [getAvailableToolsForItem]);
	const items = useAtomValue(availableToolsItemsAtom);
	const getItems = useGetItems();

	const useStore = useMemo(() => {
		return createWithEqualityFn<AvailableToolsStore>(
			(set, get) => ({
				global: getAvailableToolsForItemRef.current(),
				byGuids: new Map(),
				activeListenersCount: new Map(),
				getByGuids: (guid) => {
					const byCache = get().byGuids.get(guid);
					if (byCache) {
						return byCache;
					}
					const item = getItems().find((item) => item.guid === guid)!;
					const value = getAvailableToolsForItemRef.current(item);
					get().byGuids.set(guid, value);
					return value;
				},
			}),
			shallow
		);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useDebouncedEffect(
		() => {
			useStore.setState(() => ({
				global: getAvailableToolsForItem(),
			}));
		},
		100,
		[getAvailableToolsForItem, useStore]
	);

	useDebouncedEffect(
		() => {
			useStore.setState((prev) => {
				if (prev.activeListenersCount.size === 0) {
					return prev;
				}
				const byGuids = new Map<string, AvailableTools>();
				for (let [guid, value] of prev.activeListenersCount.entries()) {
					if (!guid) {
						continue;
					}
					if (!value) {
						continue;
					}
					const item = getItems().find((item) => item.guid === guid);
					if (item) {
						byGuids.set(guid, getAvailableToolsForItem(item));
					}
				}
				return {
					byGuids,
				};
			});
		},
		100,
		[items, getAvailableToolsForItem, useStore]
	);

	// @ts-ignore
	return <AvailableToolsContext.Provider value={useStore}>{children}</AvailableToolsContext.Provider>;
};
